import SponsorLogoPlaceholder from "./SponsorLogoPlaceholder";
import { trackEvent } from "@/lib/analytics";

/**
 * Renders a sponsor's logo (real img or placeholder) as a consistent unit.
 *
 * Phase 5F.6 — sizes accumulated from base x1.25 (5E) x1.25 (5F.5) → ~1.56x.
 *   sm  → ~62px / 78px md (mobile/desktop)
 *   md  → ~94px / 112px
 *   lg  → ~125px / 150px
 *   xl  → ~175px / 220px (lg + 40% — opening band & story-bottom strip)
 *
 * Logo source:
 *   tone="dark" → logo_dark_url (light ink) preferred
 *   tone="light" → logo_url (dark ink) preferred
 */
export default function SponsorLogo({ sponsor, tone = "light", size = "md", linkClass = "" }) {
  const isPlaceholder = !sponsor.logo_url || sponsor.status === "placeholder";

  const sizeClass = {
    sm: "h-[3.875rem] md:h-[4.875rem]",       // ~62 / 78
    md: "h-[5.875rem] md:h-[7rem]",            // ~94 / 112
    lg: "h-[7.75rem] md:h-[9.375rem]",         // ~124 / 150
    xl: "h-[11rem] md:h-[13.75rem]",           // ~176 / 220
  }[size] || "h-[5.875rem] md:h-[7rem]";

  const imgSrc = tone === "dark"
    ? (sponsor.logo_dark_url || sponsor.logo_url)
    : (sponsor.logo_url || sponsor.logo_dark_url);

  const toneFilter = tone === "dark"
    ? "brightness-110 opacity-90 hover:opacity-100 transition-opacity duration-[400ms]"
    : "opacity-90 hover:opacity-100 transition-opacity duration-[400ms]";

  const imageEl = (
    <div className={sizeClass}>
      <img
        src={imgSrc}
        alt={sponsor.name}
        className={`h-full w-auto object-contain object-center ${toneFilter}`}
        loading="lazy"
      />
    </div>
  );

  const inner = isPlaceholder ? (
    <SponsorLogoPlaceholder name={sponsor.name} tier={sponsor.tier} tone={tone} size={size} />
  ) : (
    imageEl
  );

  if (sponsor.website) {
    return (
      <a
        href={sponsor.website}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex ${linkClass}`}
        data-testid={`sponsor-link-${sponsor.slug}`}
        aria-label={sponsor.name}
        onClick={() => {
          trackEvent("sponsor_band_click", {
            sponsor_slug: sponsor.slug,
            sponsor_tier: sponsor.tier,
          });
          trackEvent("outbound_click", { destination: sponsor.website });
        }}
      >
        {inner}
      </a>
    );
  }
  return (
    <div className={`inline-flex ${linkClass}`} data-testid={`sponsor-logo-${sponsor.slug}`}>
      {inner}
    </div>
  );
}
